import * as React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ooh/ui/components/tooltip';
import type { CellEntry, PeriodGranularity } from '@ooh/contracts';
import { formatDisplayDate, formatDisplayDateRange } from '@ooh/ui/lib/date-format';
import type { CellState } from '../lib/availability-types';
import { CellStateIcon } from './availability-cell-styles';

/**
 * Builds the canonical owner-hint text for a cell's dominant entry — offerTitle,
 * then clientName, then holderName, joined by ' · ', skipping falsy parts. Single
 * source of truth for every surface that renders occupied-cell owner text
 * (inventory catalog, `DetailAvailabilityCard`, offer-draft Availability tab) — see
 * the "Consistent owner-hint label and tooltip across every availability-grid
 * surface" requirement (availability-calendar spec).
 */
export function buildEntryOwnerHint(
  entry: Pick<CellEntry, 'clientName' | 'offerTitle' | 'holderName'>,
): string {
  const parts: string[] = [];
  if (entry.offerTitle) parts.push(entry.offerTitle);
  if (entry.clientName) parts.push(entry.clientName);
  if (entry.holderName) parts.push(entry.holderName);
  return parts.join(' · ');
}

/**
 * Formats a reservation-backed entry's expiry deadline for the hover tooltip,
 * prefixed with the caller-supplied translated label (e.g. "Wygasa"). Returns
 * `undefined` — segment omitted, never substituted — for a status that can never
 * expire (`SOLD`/`OPTION`/base-state/window) or an expiring status with no deadline
 * set yet; the tooltip is a supplementary surface, the popover remains the
 * guaranteed "no deadline set" marker per the `availability-calendar` capability's
 * "expiry deadline reaches the operator through the cell popover" requirement.
 */
export function buildEntryDeadlineTooltipLabel(
  entry: Pick<CellEntry, 'status' | 'ttlExpiresAt'>,
  expiresLabelPrefix: string,
): string | undefined {
  if (entry.status !== 'SOFT_RESERVATION' && entry.status !== 'HARD_RESERVATION') return undefined;
  if (!entry.ttlExpiresAt) return undefined;
  return `${expiresLabelPrefix}: ${formatDisplayDate(entry.ttlExpiresAt)}`;
}

/**
 * Resolves the popover's date-range line: the entry's OWN booking window when
 * present, falling back to the caller-supplied label (the calendar period, or an
 * availability-window's own displayed range) when the entry carries no booking
 * window at all — base-state and availability-window entries, which have no
 * reservation. The `unified-availability-interaction` INSPECT-popover requirement
 * mandates the reservation's own first/last booked day; the availability-window
 * read-only variant legitimately keeps showing the window's own range, which this
 * fallback preserves unchanged.
 */
export function resolvePopoverDateRangeLabel(
  entry: Pick<CellEntry, 'dateFrom' | 'dateTo'> | undefined,
  fallbackLabel: string,
): string {
  if (entry?.dateFrom && entry.dateTo) {
    return formatDisplayDateRange(entry.dateFrom, entry.dateTo);
  }
  return fallbackLabel;
}

/**
 * True when a date span (a caller's selected/inspected cell range) leaves at least one
 * day of a hold's own full booking window unselected — the single shared under-coverage
 * check gating the whole-versus-selected-period release choice (CU-86cbh03pj), used by
 * both `UnifiedSelectPopover`'s `hasUnderCoveredHold` (multi-cell Zwolnij) and
 * `InspectPopover`'s Anuluj/Wymuś zwolnienie (single-cell). A span whose start is at or
 * before the hold's own start AND whose end is at or after the hold's own end fully
 * covers (or exceeds) the hold and is NOT under-covered — deliberately NOT a strict
 * containment check, since a span may itself extend outside the hold's range on one
 * side while still under-covering it on the other.
 */
export function isHoldRangeUnderCovered(
  spanFrom: Date,
  spanTo: Date,
  holdFrom: Date,
  holdTo: Date,
): boolean {
  return spanFrom > holdFrom || spanTo < holdTo;
}

/** Minimum column pixel width for the inline owner label to fit next to the state icon. */
export const AVAILABILITY_CELL_LABEL_MIN_WIDTH = 72;

/**
 * Single canonical column-pixel-width-per-granularity source, shared by every
 * availability-grid surface — the media-inventory list and media-unit detail card
 * (via `GRANULARITY_COL_WIDTH` in `availability-granularity-select.tsx`, which derives
 * its values from this map) and the offer-draft Availability tab (`availability-tab-page.tsx`,
 * keyed directly by the uppercase `PeriodGranularity` enum). Fixes the offer tab
 * rendering much wider than the other two surfaces (no shared width source before).
 *
 * `DAY` MUST stay 40 — `MultiStackCellContent` hardcodes `isDayGranularity = colWidth <= 40`
 * to trigger the spec-required "day granularity degrades to dominant entry + N" behavior
 * (see "Multi-Stack Cell Rendering" requirement, availability-calendar spec). Changing it
 * would silently break that behavioral threshold on every surface.
 */
export const AVAILABILITY_COLUMN_WIDTH: Record<PeriodGranularity, number> = {
  DAY: 40,
  WEEK: 88,
  HALF_MONTH: 104,
  MONTH: 124,
};

/**
 * Pulls the parenthesized agency/client qualifier out of `offerTitle` ITSELF, e.g.
 * "Oferta #OFR-2026-031 (Agencja Lato 2026)" -> "Agencja Lato 2026" — never out of
 * `hint`, the caller's full joined owner-hint string (`buildEntryOwnerHint`'s
 * `offerTitle · clientName · holderName`). `clientName`/`holderName` routinely carry
 * their OWN unrelated parentheses (e.g. a seeded test account literally named
 * "X (worker N)"), which a naive whole-hint regex used to misread as the short label
 * instead of the real offer identifier — verified against a raw API dump that
 * `offerTitle` itself carried the correct campaign number in that case; only the
 * extraction's scope was wrong (CU-86cbh0jhy, follow-up).
 *
 * Falls back to `offerTitle` verbatim when it has no parenthesized segment of its own
 * (e.g. a plain campaign number) — NOT to the full `hint` — so the short label still
 * reads as the offer identifier rather than the whole multi-part hint. Falls back to
 * `hint` only when `offerTitle` is absent entirely, in which case NO extraction is
 * attempted at all: a `clientName`/`holderName` parenthesis is never a candidate.
 */
export function extractShortOwnerLabel(offerTitle: string | undefined, hint: string): string {
  if (!offerTitle) return hint;
  const match = offerTitle.match(/\(([^)]+)\)/);
  return match?.[1] ?? offerTitle;
}

/**
 * Inline owner-name label shown next to the cell's state icon when the column is wide
 * enough. `onDarkFill` (true for a HARD_RESERVATION `state`) swaps the default dark
 * `text-foreground/80` for a light color so the label stays legible on the dark
 * `--status-hard` fill; the conditional-sale `text-status-sold` red is bright enough to
 * stay legible on both fills unchanged.
 */
function InlineOwnerLabel({
  text,
  conditional,
  onDarkFill,
}: {
  text: string;
  conditional: boolean;
  onDarkFill: boolean;
}) {
  const colorClass = conditional
    ? 'text-status-sold'
    : onDarkFill
      ? 'text-white/90'
      : 'text-foreground/80';
  return (
    <span
      className={`truncate text-2xs leading-tight ${colorClass}`}
      data-conditional={conditional ? 'true' : undefined}
    >
      {text}
    </span>
  );
}

/** Owner-hint text rendered inside the hover tooltip. */
function TooltipOwnerHint({ text, conditional }: { text: string; conditional: boolean }) {
  return (
    <span
      className={conditional ? 'text-status-sold' : undefined}
      data-conditional={conditional ? 'true' : undefined}
      data-testid={conditional ? 'tooltip-conditional-owner-hint' : undefined}
    >
      {text}
    </span>
  );
}

/**
 * Wraps `trigger` in the owner-hint hover tooltip, or returns it unwrapped when there's no
 * hint. Requires an ancestor `TooltipProvider` — the inventory catalog's route already wraps
 * its `RichAvailabilityCell` region with one; `AvailabilityGrid` wraps its own render tree
 * with one too, covering `DetailAvailabilityCard` and the offer-draft Availability tab.
 *
 * Shows the owner hint plus the entry's expiry deadline — NEVER the reservation's own
 * booking window (redundant with the column header the cell sits in; that's the popover's
 * job — CU-86cbc4jv7) and never the calendar period/column itself (availability-calendar
 * spec). The deadline is omitted, not substituted with anything, when the entry doesn't
 * carry one (see `buildEntryDeadlineTooltipLabel`).
 */
function wrapWithTooltip(
  trigger: React.ReactNode,
  ownerHint: string | undefined,
  ownerConditional: boolean,
  deadlineLabel: string | undefined,
): React.ReactNode {
  if (!ownerHint) return trigger;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-56 text-xs">
        <TooltipOwnerHint text={ownerHint} conditional={ownerConditional} />
        {deadlineLabel !== undefined ? ` · ${deadlineLabel}` : null}
      </TooltipContent>
    </Tooltip>
  );
}

export interface AvailabilityCellVisualProps {
  state: CellState;
  ownerHint?: string;
  /**
   * The dominant entry's raw `offerTitle`, used ONLY to scope the inline short-label
   * extraction (`extractShortOwnerLabel`) — never rendered directly. Omit when the
   * entry carries no `offerTitle` (base-state, or a hold not linked to any offer); the
   * inline label then falls back to the full `ownerHint` rather than misreading a
   * `clientName`/`holderName` parenthesis as the short label (CU-86cbh0jhy, follow-up).
   */
  offerTitle?: string;
  /**
   * True when the entry backing `ownerHint` is a conditional sale (HARD_RESERVATION
   * with `conditional: true`) — applies the red-font treatment to the inline label and
   * tooltip content.
   */
  ownerConditional?: boolean;
  /**
   * The dominant entry's expiry deadline, pre-formatted (with a translated prefix) by
   * the caller via `buildEntryDeadlineTooltipLabel`. Omit when the entry's status never
   * expires or has no deadline set.
   */
  deadlineLabel?: string;
  /** Column pixel width — controls whether the inline owner label fits next to the icon. */
  colWidth: number;
  soldAriaLabel?: string;
  optionAriaLabel?: string;
  reservedAriaLabel?: string;
}

/**
 * Single shared cell-visual — the status icon, plus an inline truncated owner-name
 * label (when the column is wide enough and owner data exists), plus a hover tooltip
 * with the full owner hint and its expiry deadline (when owner data exists). Renders
 * the icon only when `ownerHint` is empty/undefined. Every surface rendering occupied
 * availability-grid cells (inventory catalog, `DetailAvailabilityCard`, offer-draft
 * Availability tab) MUST render through this one component instead of maintaining its
 * own icon/label/tooltip composition — see the "Consistent owner-hint label and tooltip
 * across every availability-grid surface" requirement.
 */
export function AvailabilityCellVisual({
  state,
  ownerHint,
  offerTitle,
  ownerConditional = false,
  deadlineLabel,
  colWidth,
  soldAriaLabel,
  optionAriaLabel,
  reservedAriaLabel,
}: AvailabilityCellVisualProps) {
  const showInlineLabel = Boolean(ownerHint) && colWidth >= AVAILABILITY_CELL_LABEL_MIN_WIDTH;
  const shortLabel = ownerHint ? extractShortOwnerLabel(offerTitle, ownerHint) : undefined;
  const onDarkFill = state === 'reserved-own-hard' || state === 'reserved-other-hard';
  const content = (
    <span className="flex items-center gap-1 overflow-hidden">
      <CellStateIcon
        state={state}
        soldAriaLabel={soldAriaLabel}
        optionAriaLabel={optionAriaLabel}
        reservedAriaLabel={reservedAriaLabel}
      />
      {showInlineLabel ? (
        <InlineOwnerLabel
          text={shortLabel ?? ''}
          conditional={ownerConditional}
          onDarkFill={onDarkFill}
        />
      ) : null}
    </span>
  );
  return wrapWithTooltip(content, ownerHint, ownerConditional, deadlineLabel);
}
